import type { HiringRaisePreview } from '../../services/api';
import { InfoDot, KeyValues } from './HiringForm';
import {
  REVIEW_WORDS,
  existingHiringLines,
  localiseRaiseMessage,
  reviewTeamPairs,
  streetOfPickedAddress,
} from './hiringText';

/**
 * The review of a raise (panel 4 of the approved mock-up, 15 Oct 2026): "Raise this hiring?",
 * a few label-and-value lines (Opening for, Replaces, Budget, Address, Team), then at most
 * two notes. The server's own lines - what Recruitment will do, word for word - are behind
 * the one (i) beside the heading, so nothing it said is lost.
 *
 * The address is the one the review says will be SENT (the server's `opening`), where the
 * answer carries it; otherwise the form's. It reads "street · city · PIN": an address that
 * already ends with its city, state, PIN code or "India" does not say them twice. The team is the server's, by name, as reviewed.
 * Used by the Raise hiring window (raise and ask mode) and by the confirmer's window.
 */

/** The server's review answer: its `opening` and `budget` are read defensively, never required. */
type PreviewFacts = HiringRaisePreview;

export interface RaiseReviewFacts {
  /** "Gadget Hub Indiranagar (GHI-03)", or "No store". */
  openingFor: string;
  /** "Kavya Rao · 910482 · Notice 31 Oct"; null when nobody is replaced. */
  replaces: string | null;
  budgetName: string;
  /** Free positions as the form had them; the review's own count wins. */
  budgetFree: number | null;
  /** The address as the form has it, when the review does not say. */
  address: string | null;
  subjectKind: 'store' | 'role';
}

const sentAddress = (preview: PreviewFacts): string | null => {
  const opening = preview.opening;
  if (!opening) return null;
  const street = streetOfPickedAddress(String(opening.location ?? ''), { city: opening.cityName, state: opening.stateName });
  const parts = [street, opening.cityName, opening.pincode == null ? null : String(opening.pincode)]
    .map((part) => String(part ?? '').trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/** One clause after the person replaced: whether 1Office records it as their replacement. */
const replacementClause = (preview: HiringRaisePreview): string | null => {
  const r = preview.replacement;
  if (!r) return null;
  if (r.markerSent || r.alreadyMarked) return 'recorded as their replacement';
  return 'a plain opening';
};

/** The server's line about the person replaced, for the clause's hover. */
const replacementSentence = (preview: HiringRaisePreview): string | null => {
  const code = preview.replacement?.empCode;
  if (!code) return null;
  const line = (preview.sentences ?? []).find((s) => s.includes(code) && !s.startsWith('This '));
  return line ? localiseRaiseMessage(line) : null;
};

export function RaiseReview({ preview, asking, facts, heading }: {
  preview: HiringRaisePreview;
  /** The asker's review: the team is the confirmer's to pick, and nothing goes out until it is confirmed. */
  asking: boolean;
  facts: RaiseReviewFacts;
  heading?: string;
}) {
  const p = preview as PreviewFacts;
  const free = typeof p.budget?.free === 'number' ? p.budget.free : facts.budgetFree;
  const budget = free == null
    ? facts.budgetName
    : <>{facts.budgetName} <span className="text-gray-500">· {asking ? REVIEW_WORDS.freeNow(free) : REVIEW_WORDS.usesOne(free)}</span></>;
  const clause = replacementClause(preview);
  const replaces = facts.replaces
    ? <>{facts.replaces}{clause && <span className="text-gray-500" title={replacementSentence(preview) ?? undefined}>{` · ${clause}`}</span>}</>
    : null;
  const team = preview.team ? (
    <div className="grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-0.5">
      {reviewTeamPairs(preview.team).map((pair) => (
        <div key={pair.label} className="contents">
          <span className="text-gray-500">{pair.label}</span>
          <span>{pair.value}</span>
        </div>
      ))}
    </div>
  ) : asking ? REVIEW_WORDS.teamByConfirmer : null;

  const existing = existingHiringLines(preview, facts.subjectKind);
  // Asking: the foot already says nothing is sent until someone confirms it - said once, not twice.
  const notes = [...(asking ? [] : [REVIEW_WORDS.noteLive]), existing[0] ?? REVIEW_WORDS.noteCancel];
  // The server's own lines, word for word, then whom Recruitment mails, then any hiring already there beyond the note.
  const serverLines = [
    ...(preview.sentences ?? []).map(localiseRaiseMessage),
    ...(preview.team?.mailSentence ? [localiseRaiseMessage(preview.team.mailSentence)] : []),
    ...existing.slice(1),
  ].join(' ');

  return (
    <div className="text-sm">
      <h4 className="mb-3.5 mt-1 flex items-center gap-2 text-base font-semibold text-gray-900">
        {heading ?? (asking ? REVIEW_WORDS.askHeading : REVIEW_WORDS.heading)}
        {serverLines && <InfoDot text={serverLines} label={REVIEW_WORDS.infoLabel} />}
      </h4>
      <KeyValues
        label={REVIEW_WORDS.infoLabel}
        rows={[
          { key: 'for', label: REVIEW_WORDS.openingFor, value: facts.openingFor },
          { key: 'replaces', label: REVIEW_WORDS.replaces, value: replaces },
          { key: 'budget', label: REVIEW_WORDS.budget, value: budget },
          { key: 'address', label: REVIEW_WORDS.address, value: sentAddress(p) ?? facts.address },
          { key: 'team', label: REVIEW_WORDS.team, value: team },
        ]}
      />
      <ul aria-label="Notes" className="mt-4 list-disc space-y-0.5 border-t border-gray-100 pl-5 pt-3 text-[13px] text-gray-600">
        {notes.map((note) => <li key={note}>{note}</li>)}
      </ul>
    </div>
  );
}

export default RaiseReview;
